// Predesigned PT sessions for a unit training on a 1/3-mile (~536 m) field track,
// bodyweight only, for the Air Force fitness test (2-mile run + 1-min push-ups +
// 1-min sit-ups/cross-leg crunch + forearm plank). A "workout" isn't a stored
// entity — it just prefills the Create Event form (title, type, duration, notes)
// and, when it carries a `segments` list, can be run as a guided session (see the
// WorkoutPlayer). Add or edit entries here; they show up in the "Load a workout…"
// picker.
// ponytail: plain constant, no schema. If units ever need their own saved
// templates, promote to a DB table then.

// Segment builders for the guided player. `timed` = fixed-duration block (warm-
// up, work interval, rest, a calisthenics station). `laps` = hand off to the heat
// stopwatch for `lapCount` laps; it's one leader-paced block — the leader runs
// all reps + rests on the stopwatch and taps Next when the group's done.
// `repeat` expands rounds at load so `workout.segments` is always a flat list.
// `say` overrides the spoken cue when the segment name isn't a clean phrase.
// ponytail: no inter-rep rest modeled inside a lap block — the leader manages it.
const timed = (name, seconds, say) => ({ kind: "timed", name, seconds, ...(say ? { say } : {}) });
// A rest/transition block. `rest: true` tells the player to announce the next
// exercise during it ("Rest. Next up, sit-ups.") so the group knows what's coming.
const rest = (name, seconds, say) => ({ ...timed(name, seconds, say), rest: true });
const laps = (name, lapCount, say) => ({ kind: "laps", name, laps: lapCount, ...(say ? { say } : {}) });
// Stamps round/rounds on each cloned segment so the player can show "Round 3 of 5".
const repeat = (n, segs) => Array.from({ length: n }, (_, r) =>
  segs.map((s) => ({ ...s, round: r + 1, rounds: n }))).flat();

// One 4-minute Tabata block: 8x (20s work / 10s rest). The first interval speaks
// the exercise name (also shown big on screen); the rest just say "Work." so the
// audio isn't 8x repetitive. ponytail: same exercise all block, name it once. The
// reps aren't "rounds", so strip the round stamps repeat() adds.
const tabata = (name) => {
  const strip = ({ round, rounds, ...s }) => s;
  return [
    timed(name, 20, `${name}.`), rest("Rest", 10),
    ...repeat(7, [timed(name, 20, "Work."), rest("Rest", 10)]).map(strip)
  ];
};

export const WORKOUT_PRESETS = [
  {
    id: "lap-repeats",
    label: "Speed · Lap Repeats",
    title: "Lap Repeats",
    eventType: "PT",
    durationMinutes: 45,
    notes: `WARM-UP (12 min)
- 2 laps (~1070m) easy jog
- Dynamic drills on the infield: leg swings, high knees, butt kicks, A-skips
- 3x ~half-straight strides building to ~80%

MAIN SET — 8x 1 lap (~536m)
- Run each lap hard, a touch faster than 2-mile goal pace
- Rest: walk a half lap between reps (~90s)
- Hold even splits; if you fade more than 5s/lap, stop at 6 reps

COOL-DOWN (8 min)
- 1-2 laps walk/jog
- Calf, quad, hip-flexor, hamstring stretch

Coaching: pace off the start line, no sprinting the first 100m. Stagger fast/slow runners in heats so the track stays clear. Log each lap on the stopwatch.`,
    segments: [
      timed("Warm-up jog", 720),
      timed("Dynamic drills", 180),
      timed("Strides", 120),
      laps("Lap repeats", 8, "Eight one-lap repeats. Run a touch faster than two-mile pace. Go."),
      timed("Cool-down walk", 480)
    ]
  },
  {
    id: "two-lap-intervals",
    label: "VO2 · Two-Lap Intervals",
    title: "Two-Lap Intervals",
    eventType: "PT",
    durationMinutes: 50,
    notes: `WARM-UP (14 min)
- 2 laps easy jog
- Dynamic drills + 2x half-straight strides

MAIN SET — 4x 2 laps (~1070m)
- Each rep at hard 2-mile race effort
- Rest: 2-3 min walk between reps
- Goal: all reps within 5s of each other

COOL-DOWN (8 min)
- 1-2 laps jog + full stretch

Coaching: this builds the engine for the 2-mile. Log splits and compare week to week.`,
    segments: [
      timed("Warm-up jog", 720),
      timed("Strides", 120),
      laps("Two-lap intervals", 8, "Four two-lap intervals at two-mile race effort. Go."),
      timed("Cool-down jog", 480)
    ]
  },
  {
    id: "tempo-run",
    label: "Threshold · Tempo Run",
    title: "Tempo Run",
    eventType: "PT",
    durationMinutes: 45,
    notes: `WARM-UP (12 min)
- 2 laps easy jog
- 4x half-straight strides

MAIN SET — 20 min tempo
- Continuous run at "comfortably hard" (can speak 3-4 words, not a sentence)
- ~25-30s/mile slower than 2-mile pace
- Count laps; aim to negative-split the back half

COOL-DOWN (8 min)
- 1-2 laps easy + stretch

Coaching: effort-based, not a race. Keep the group in a pack to hold pace honest.`,
    segments: [
      timed("Warm-up jog", 600),
      timed("Strides", 120),
      timed("Tempo run", 1200),
      timed("Cool-down jog", 480)
    ]
  },
  {
    id: "fartlek",
    label: "Fartlek · Speed Play",
    title: "Fartlek Session",
    eventType: "PT",
    durationMinutes: 40,
    notes: `WARM-UP (10 min)
- 2 laps easy jog + dynamic drills

MAIN SET — 8x (1 min hard / 2 min easy) = 24 min
- Surge on the straights, float the bends
- Keep moving the entire block — no full stops

COOL-DOWN (6 min)
- 1 lap easy + stretch

Coaching: unstructured and fun. The player calls each surge and float so the whole group changes gears together.`,
    segments: [
      timed("Warm-up jog", 600),
      ...repeat(8, [timed("Hard surge", 60), rest("Easy float", 120)]),
      timed("Cool-down jog", 360)
    ]
  },
  {
    id: "sprint-strides",
    label: "Speed · Sprints & Strides",
    title: "Sprints & Strides",
    eventType: "PT",
    durationMinutes: 42,
    notes: `WARM-UP (13 min)
- 1 lap jog + dynamic drills
- 3x accelerations to 90%

MAIN SET (on the straights)
- 6x ~40m sprints, walk-back recovery
- Easy recovery jog
- 6x flying strides (~60m), walk-back recovery

COOL-DOWN (5 min)
- Easy jog + stretch

Coaching: quality over quantity — full recovery between sprints. All on the track straights; no cones needed.`,
    segments: [
      timed("Warm-up jog", 600),
      timed("Accelerations", 180),
      ...repeat(6, [timed("Sprint", 20), rest("Walk-back recovery", 70)]),
      rest("Recovery jog", 180),
      ...repeat(6, [timed("Flying stride", 25), rest("Walk-back recovery", 95)]),
      timed("Cool-down jog", 300)
    ]
  },
  {
    id: "strength-circuit",
    label: "Strength · Rotating Circuit",
    title: "Bodyweight Rotating Circuit",
    eventType: "PT",
    durationMinutes: 45,
    notes: `WARM-UP (6 min)
- 1 lap jog + arm circles, push-up-to-down-dog, bodyweight squats

MAIN SET — 5 rounds, 45s work / 15s rotate
1. Push-ups (push)
2. Air squats (legs)
3. Sit-ups / cross-leg reverse crunch (core)
4. Reverse lunges (legs)
5. Hand-release push-ups (push)
6. Forearm plank (core)

COOL-DOWN (5 min)
- Walk + full-body stretch

Coaching: stations rotate so no muscle group works back-to-back, biased toward the test's push-ups + core. Cue full reps over speed; count honest reps.`,
    segments: [
      timed("Warm-up jog", 360),
      ...repeat(5, [
        timed("Push-ups", 45), rest("Rotate", 15),
        timed("Air squats", 45), rest("Rotate", 15),
        timed("Sit-ups", 45), rest("Rotate", 15),
        timed("Reverse lunges", 45), rest("Rotate", 15),
        timed("Hand-release push-ups", 45), rest("Rotate", 15),
        timed("Forearm plank", 45), rest("Rotate", 15)
      ]),
      timed("Cool-down walk", 300)
    ]
  },
  {
    id: "tabata-strength",
    label: "Strength · Tabata",
    title: "Bodyweight Tabata",
    eventType: "PT",
    durationMinutes: 38,
    notes: `WARM-UP (6 min)
- 1 lap jog + dynamic mobility

MAIN SET — 6 Tabata blocks, each 8x (20s hard / 10s rest), 30s between blocks
1. Push-ups (push)
2. Air squats (legs)
3. Sit-ups (core)
4. Plank shoulder taps (push + core)
5. Jump squats (legs)
6. Flutter kicks (core)

COOL-DOWN (5 min)
- Walk + full-body stretch

Coaching: groups rotate each block so nothing stacks back-to-back. Go all-out the 20s, full rest the 10s. The player calls every work and rest interval.`,
    segments: [
      timed("Warm-up jog", 360),
      ...tabata("Push-ups"),
      rest("Block rest", 30),
      ...tabata("Air squats"),
      rest("Block rest", 30),
      ...tabata("Sit-ups"),
      rest("Block rest", 30),
      ...tabata("Plank shoulder taps"),
      rest("Block rest", 30),
      ...tabata("Jump squats"),
      rest("Block rest", 30),
      ...tabata("Flutter kicks"),
      timed("Cool-down walk", 300)
    ]
  },
  {
    id: "pyramid-ladder",
    label: "Mixed · Pyramid Ladder (self-paced)",
    title: "Pyramid Ladder",
    eventType: "PT",
    durationMinutes: 50,
    notes: `WARM-UP (12 min)
- 2 laps jog + dynamic drills + 2 strides

MAIN SET — half lap / 1 lap / 1.5 laps / 1 lap / half lap
- Each rep hard but controlled (longer reps slightly slower)
- Rest: walk equal distance to the rep just run
- ~4.5 laps of work (~2400m)

COOL-DOWN (8 min)
- 1-2 laps walk/jog + stretch

Coaching: self-paced — wide ability spread, everyone runs the same structure at their own effort. Run freeform; use the stopwatch only if you want splits.`
  },
  {
    id: "recovery-base",
    label: "Recovery · Base Run + Mobility (self-paced)",
    title: "Recovery Run & Mobility",
    eventType: "PT",
    durationMinutes: 40,
    notes: `WARM-UP (5 min)
- Brisk walk + leg swings

MAIN SET
- 25-30 min easy conversational run (full sentences the whole time)
- Keep heart rate low — this is active recovery, not training

MOBILITY (10 min)
- Hips, calves, hamstrings, quads, T-spine
- Hold each stretch 30s

Coaching: self-paced by effort, not a timer. Schedule the day after a hard interval or test. Slow is the point — no racing.`
  },
  {
    id: "test-sim",
    label: "Diagnostic · 2-Mile Time Trial (self-paced)",
    title: "2-Mile Time Trial + Strength",
    eventType: "PT",
    durationMinutes: 50,
    notes: `WARM-UP (15 min)
- 2 laps easy jog
- Dynamic drills + 3x half-straight strides
- Settle nerves; review pacing plan

MAIN SET
- 2-mile time trial = 6 laps (~3220m) — run for time, even pacing
- Rest 8-10 min
- Strength check: 1 min max push-ups, 1 min max sit-ups (or plank to failure)

COOL-DOWN (8 min)
- 1-2 laps walk + stretch

Coaching: practice run, not the official test — log results to track readiness. Run it self-paced; stagger starts in heats and record lap splits on the stopwatch.`
  }
];

// ── Guided-session helpers ───────────────────────────────────────────────────
// How fast to play the spoken cues. Applied at playback (HTMLAudioElement
// .playbackRate / SpeechSynthesis .rate), NOT baked into the clips — so it's one
// knob to retune with no GPU regeneration. 1.0 = as-recorded.
// ponytail: playback-rate knob, not re-rendered audio. Bake into the clips only
// if a browser ever ignores playbackRate on the fallback path.
export const PLAYBACK_RATE = 1.03;

// Spoken cues that aren't tied to a specific segment (countdown + transitions).
// The player speaks the countdown digits one per second.
export const SYSTEM_CUES = [
  "Get ready.", "five", "four", "three", "two", "one", "Go.", "Next up.", "Workout complete."
];

// The exact spoken line for a segment / a workout intro. BOTH the player and the
// clip generator import these so the generated phrase and the looked-up phrase
// match byte-for-byte — change here and regenerate.
export const cueFor = (segment) => segment.say || `${segment.name}.`;
export const introFor = (workout) => `Starting ${workout.title}.`;

export const runnableWorkouts = WORKOUT_PRESETS.filter(
  (w) => Array.isArray(w.segments) && w.segments.length > 0
);
export const findWorkout = (id) => WORKOUT_PRESETS.find((w) => w.id === id) || null;

// Fixed clock time (laps are open-ended, so excluded). Used for the overview.
export const timedTotalSeconds = (workout) =>
  (workout.segments || [])
    .filter((s) => s.kind === "timed")
    .reduce((total, s) => total + s.seconds, 0);

// Wall-clock catch-up for the guided player. From the current segment index and
// the absolute ms it started, fast-forward across any TIMED segments whose time
// fully elapsed by nowMs. Stops at a lap segment (the clock waits for the leader)
// or the end — so a backgrounded/locked phone resumes in the right place. Returns
// the segment to show now and when it (notionally) started.
export const catchUp = (segments, index, startedMs, nowMs) => {
  while (index < segments.length && segments[index].kind === "timed") {
    const endMs = startedMs + segments[index].seconds * 1000;
    if (nowMs < endMs) break;
    startedMs = endMs; // carry the remainder into the next segment
    index += 1;
  }
  return { index, startedMs };
};

// Every distinct phrase the generator must render for a set of workouts.
export const collectCues = (workouts = runnableWorkouts) => {
  const cues = new Set(SYSTEM_CUES);
  for (const workout of workouts) {
    cues.add(introFor(workout));
    for (const segment of workout.segments) {
      cues.add(cueFor(segment));
      // Bare name too, so the "Next up, <exercise>" rest announcement always has a
      // clip even when the segment's own cue is an override like "Work.".
      cues.add(`${segment.name}.`);
    }
  }
  return [...cues];
};
